import { QueryBuilder, type QueryFactory } from '@medapp/silo'
import { convertToFirestoreQuery } from '@medapp/silo/firestore-app'
import {
  type DocumentData,
  type Query,
  type DocumentReference,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  collection as firestoreCollection,
  type CollectionReference,
  getDocs,
  onSnapshot,
  getDoc,
  doc as docRef,
  DocumentSnapshot,
  startAfter,
  getFirestore,
} from 'firebase/firestore'
import { pascalCase } from 'scule'
import type { UnknownRecord } from '~/utils/mapper'

let firestoreListeners = 0

const MAX_LIMIT = 1000
const CURSOR = Symbol('Cursor')

export interface Cursor {
  readonly isCursor: unique symbol
}

export function useFirestore() {
  // TODO: initialize here instead of somewhere else.
  const firestore = getFirestore()

  function collection<T>(...name: string[]): CollectionReference<T>
  function collection<T>(...name: string[]) {
    return firestoreCollection(
      firestore,
      name.join('/'),
    ) as CollectionReference<T>
  }

  function mapField(field: string) {
    return field
      .split('.')
      .map(part => pascalCase(part))
      .join('.')
  }

  async function mapDocument<T, R>(
    doc: DocumentSnapshot<T, DocumentData>,
    map?: (document: T) => R | Promise<R>,
  ): Promise<R> {
    if (!doc.exists()) {
      throw new MissingDocumentError(doc)
    }

    const data = Mapper.toCamelCase(doc.data()!)
    // Add metadata, and make it not enumerable so it isn't included in the serialized output
    Object.defineProperties(data, {
      id: {
        value: doc.id,
        enumerable: true,
      },
      ref: {
        value: doc.ref,
        enumerable: false,
      },
    })

    return (await map?.(data as T)) ?? (data as unknown as R)
  }

  interface QueryOptions<T> {
    /**
     * the limit of this query
     * @default 250
     */
    limit?: number
    sort?: SortOptions<keyof T | (string & {})> | SortOptions<keyof T | (string & {})>[]
    /**
     * Start from this cursor. You can retrieve a cursor using getCursor from this function.
     */
    cursor?: Cursor

    /**
     * Log debug information
     */
    debug?: boolean
  }

  /**
   * Build a firestore query using the given collection and factory, with optional limit and sort options.
   * @param collectionName the collection to filter on
   * @param factory the filters to apply to this filter
   * @param options additional options like limit and sorts
   * @returns a firestore query
   */
  function query<T extends UnknownRecord>(
    collectionName: string | CollectionReference<T>,
    factory: QueryFactory<T>,
    options?: QueryOptions<T>,
  ) {
    const builder = new QueryBuilder<T>({
      mapField,
      mapValue: value => deepUnref(value),
    })

    const query = builder.query(factory)

    query.limit(options?.limit ?? MAX_LIMIT)

    if (options?.sort) {
      if (Array.isArray(options.sort)) {
        for (const sort of options.sort) {
          query.orderBy(mapField(sort.name as string), sort.direction)
        }
      }
      else {
        query.orderBy(mapField(options.sort.name as string), options.sort.direction)
      }
    }

    if (options?.debug) {
      console.debug('query:', query.toString())
    }

    const _collection
      = typeof collectionName === 'string'
        ? collection<T>(collectionName)
        : collectionName

    const fq = convertToFirestoreQuery(_collection, query, {
      append() {
        if (options?.cursor) {
          return [startAfter(options.cursor)]
        }

        return []
      },
    })
    return fq
  }

  type QuerySnapshotCallback<T> = (
    docs: QueryDocumentSnapshot<T>[],
  ) => Promise<void> | void

  type DocumentSnapshotCallback<T> = (
    docs: DocumentSnapshot<T>,
  ) => Promise<void> | void

  function startListener<T>(
    source: Query<T>,
    callback: QuerySnapshotCallback<T>,
    signal?: AbortSignal,
  ): () => void
  function startListener<T>(
    source: DocumentReference<T>,
    callback: DocumentSnapshotCallback<T>,
    signal?: AbortSignal,
  ): () => void
  function startListener<T>(
    source: Query<T> | DocumentReference<T>,
    callback: QuerySnapshotCallback<T> | DocumentSnapshotCallback<T>,
    signal?: AbortSignal,
  ) {
    firestoreListeners++

    if ('FIRESTORE_DEBUG' in window) {
      if ('path' in source)
        console.trace('Started listening to ' + source.path)
    }

    const stopListening = onSnapshot(
      source as any,
      (snapshot: QuerySnapshot<T, DocumentData> | DocumentSnapshot<T>) => {
        if (snapshot instanceof DocumentSnapshot) {
          void (callback as DocumentSnapshotCallback<T>)(snapshot)
        }
        else {
          void (callback as QuerySnapshotCallback<T>)(snapshot.docs)
        }
      },
    )

    function unsubscribe() {
      stopListening()
      firestoreListeners--

      if ('FIRESTORE_DEBUG' in window) {
        console.debug('Listeners: ' + firestoreListeners)
        if ('path' in source)
          console.trace('Stopped listening to ' + source.path)
      }
    }

    if (signal) {
      signal.addEventListener('abort', unsubscribe, { once: true })
    }

    tryOnScopeDispose(unsubscribe)

    return unsubscribe
  }

  interface GetDocumentOptions<T, R> {
    target?: Ref<R>
    map?: (document: T) => R | Promise<R>
    /**
     * @deprecated don't pass a signal when you want to run once.
     */
    once?: boolean
    signal?: AbortSignal
  }

  interface GetDocumentsOptions<T, R> {
    target?: Ref<R[]>
    map?: (document: T) => R | Promise<R>
    /**
     * @deprecated don't pass a signal when you want to run once.
     */
    once?: boolean
    signal?: AbortSignal
  }

  async function getDocuments<T, R = T>(
    query: Query<T, DocumentData> | CollectionReference<T>,
    options?: GetDocumentsOptions<T, R>,
  ): Promise<R[]> {
    options?.signal?.throwIfAborted()

    const target = options?.target ?? shallowRef([] as R[])

    // attach listener if necessary
    if (options?.signal) {
      startListener(
        query,
        async (docs) => {
          const newDocs = await Promise.all(
            docs.map(document => mapDocument(document, options?.map)),
          )

          const cursor = docs.at(-1) as unknown as Cursor

          Object.assign(newDocs, {
            [CURSOR]: cursor,
          })

          target.value = newDocs
        },
        options?.signal,
      )
    }

    // and fetch initial docs
    const { docs } = await getDocs(query)

    target.value = await Promise.all(
      docs.map(document => mapDocument(document, options?.map)),
    )

    const cursor = docs.at(-1) as unknown as Cursor

    Object.assign(target.value, {
      [CURSOR]: cursor,
    })

    return target.value
  }

  async function getDocument<T, R = T>(
    ref: string[] | Query<T, DocumentData>,
    options?: GetDocumentOptions<T, R>,
  ): Promise<R> {
    if (Array.isArray(ref)) {
      return await getDocumentByPath(ref, options)
    }

    return await getDocumentByQuery(ref, options)
  }

  async function getDocumentByPath<T, R = T>(
    path: string[],
    options?: GetDocumentOptions<T, R>,
  ) {
    options?.signal?.throwIfAborted()
    const target = options?.target ?? shallowRef(undefined as R)

    const reference = docRef(
      firestore,
      path[0],
      ...path.slice(1),
    ) as DocumentReference<T, DocumentData>

    if (options?.signal) {
      startListener(
        reference,
        async (doc) => {
          target.value = await mapDocument(doc, options?.map)
        },
        options?.signal,
      )
    }
    const doc = await getDoc(reference)
    target.value = await mapDocument(doc, options?.map)

    return target.value
  }

  async function getDocumentByQuery<T, R = T>(
    query: Query<T, DocumentData>,
    options?: GetDocumentOptions<T, R>,
  ) {
    options?.signal?.throwIfAborted()
    const target = options?.target ?? shallowRef(undefined as R)

    const { docs } = await getDocs(query)

    const first = docs.at(0)
    if (first) {
      target.value = await mapDocument(first, options?.map)
    }

    if (!options?.signal) {
      return target.value
    }

    const unsubscribe = onSnapshot(query, (snapshot) => {
      const first = snapshot.docs.at(0)

      if (!first?.exists()) return

      void mapDocument(first, options?.map).then(x => (target.value = x))
    })

    options?.signal?.addEventListener('abort', () => unsubscribe(), {
      once: true,
    })

    tryOnScopeDispose(unsubscribe)

    return target.value
  }

  function getCursor(target: Ref<unknown[]>) {
    return (target.value as any)[CURSOR] ?? null
  }

  return {
    firestore,
    collection,
    query,
    getDocuments,
    getDocument,
    getCursor,
  }
}
